import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Icon, Input, InputGroup, InputLeftElement, Spinner, Text } from "@chakra-ui/react";
import {
  HiOutlineMagnifyingGlass,
  HiCheck,
  HiOutlineXMark,
  HiOutlineArrowUp,
  HiOutlineArrowLeft,
  HiOutlineBolt,
  HiOutlineUser,
  HiOutlineLockClosed,
  HiOutlineRectangleStack,
} from "react-icons/hi2";
import { c } from "../../classroomColors";
import { STANDE } from "../../standeTokens";
import { activityApi } from "./activityApi";
import { questionLabel } from "./useLiveActivity";
import { Chip, Eyebrow, blueButton, inkButton, quietButton } from "./parts";

const MODES = [
  {
    id: "QUIZ_RACE",
    title: "Quiz race",
    line: "One question at a time. Faster full marks score higher, with a leaderboard between.",
    icon: HiOutlineBolt,
  },
  {
    id: "SOLO_SOLVE",
    title: "Solo solve",
    line: "Every question at once. Each student at their own pace, marked as they submit.",
    icon: HiOutlineUser,
  },
  {
    id: "ESCAPE_ROOM",
    title: "Escape room",
    line: "One team, a lock per question. Each full-marks answer gives up a digit of the code.",
    icon: HiOutlineLockClosed,
    max: 6,
  },
  {
    id: "BOARD",
    title: "Drop on board",
    line: "The questions land on the canvas, for the class to work through together.",
    icon: HiOutlineRectangleStack,
  },
];

const TIMERS = [
  { value: null, label: "Off" },
  { value: 60, label: "60s" },
  { value: 90, label: "90s" },
  { value: 180, label: "3m" },
];

const averageTone = (avg) => (avg == null ? null : avg >= 70 ? "green" : avg >= 45 ? "yellow" : "red");

/** One question from the bank: what it is, how the class did on it, whether it is picked. */
const BankCard = ({ q, isPicked, onToggle }) => (
  <Flex
    as="button"
    type="button"
    onClick={onToggle}
    aria-pressed={isPicked}
    textAlign="left"
    gap="12px"
    p="12px 14px"
    borderRadius="14px"
    bg={isPicked ? c.surface : c.glassEdge}
    border="1px solid"
    borderColor={isPicked ? STANDE.primaryBlue : c.borderSoft}
    boxShadow={isPicked ? `0 0 0 1px ${STANDE.primaryBlue}` : "none"}
    transition="border-color 0.12s, background 0.12s"
    _hover={isPicked ? {} : { bg: c.surface }}
    w="100%"
  >
    <Box flex="1" minW={0}>
      <Flex gap="6px" wrap="wrap" align="center" mb="6px">
        <Chip tone="blue" fontWeight="600">{questionLabel(q)}</Chip>
        <Chip>{q.total_mark} {q.total_mark === 1 ? "mark" : "marks"}</Chip>
        {q.is_mcq && <Chip>Multiple choice</Chip>}
        {q.topic_name && <Chip>{q.topic_name}</Chip>}
      </Flex>
      <Text fontSize="13.5px" color={c.text} lineHeight="1.45" noOfLines={2}>
        {q.question_text}
      </Text>
      <Text fontSize="11px" color={c.textMuted} mt="4px">
        {[q.paper_name, q.exam_series_year].filter(Boolean).join(" · ")}
      </Text>
    </Box>
    <Flex direction="column" align="flex-end" justify="space-between" flexShrink={0} gap="8px">
      <Flex
        boxSize="24px"
        borderRadius="full"
        align="center"
        justify="center"
        bg={isPicked ? STANDE.primaryBlue : "transparent"}
        border="1.5px solid"
        borderColor={isPicked ? STANDE.primaryBlue : c.border}
        color={STANDE.black}
      >
        {isPicked && <Icon as={HiCheck} boxSize="14px" />}
      </Flex>
      {q.class_average != null && (
        <Chip tone={averageTone(q.class_average)} title="How this class did before">
          class {q.class_average}%
        </Chip>
      )}
    </Flex>
  </Flex>
);

/**
 * Setting up an activity, in two steps (the teacher's, from the dock).
 *
 * The prototype put the bank, the tray, the modes and the marking on one
 * screen at once. Here they come one after another, each with the room to
 * be read: first *what* — questions from the class's own papers, searched
 * and filtered by topic, with how this class has done on them before —
 * then *how* — the order, the kind of activity, the clock — and Launch.
 * What happens after (answers, marking, release) is the live panel's job.
 */
const ActivityStudio = ({ sessionGuid, onLaunch, onDropOnBoard, busy, error, onBack, replacing }) => {
  const [step, setStep] = useState("pick");
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState("QUIZ_RACE");
  const [timer, setTimer] = useState(90);

  // Search waits for typing to pause, so the bank is asked once per word.
  useEffect(() => {
    if (!sessionGuid) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await activityApi.bank(sessionGuid, { q: search.trim(), topic });
        if (live) {
          setBank(data);
          setBankError(null);
        }
      } catch (e) {
        if (live) setBankError(e.message);
      } finally {
        if (live) setLoading(false);
      }
    }, 280);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [sessionGuid, search, topic]);

  const pickedIds = useMemo(() => new Set(picked.map((q) => q.guid)), [picked]);
  const marks = picked.reduce((sum, q) => sum + (q.total_mark || 0), 0);
  const toggle = (q) =>
    setPicked((prev) => (prev.some((p) => p.guid === q.guid) ? prev.filter((p) => p.guid !== q.guid) : [...prev, q]));
  const modeInfo = MODES.find((m) => m.id === mode);
  const tooMany = modeInfo.max && picked.length > modeInfo.max;

  if (!sessionGuid) {
    return (
      <Box px="16px" pb="16px">
        <Text fontSize="13px" color={c.textSub}>Activities need a live session to run in.</Text>
      </Box>
    );
  }

  if (step === "launch") {
    return (
      <Flex direction="column" minH={0} flex="1 1 auto">
        <Flex direction="column" gap="16px" px="16px" pb="12px" flex="1 1 auto" minH={0} overflowY="auto" data-own-scroll>
          <Box>
            <Eyebrow mb="8px">In this order</Eyebrow>
            <Flex direction="column" gap="6px">
              {picked.map((q, i) => (
                <Flex key={q.guid} align="center" gap="10px" p="9px 10px" borderRadius="12px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
                  <Flex boxSize="22px" borderRadius="full" bg={c.infoSoft} color={c.infoText} align="center" justify="center" fontSize="11px" fontWeight="600" flexShrink={0}>
                    {i + 1}
                  </Flex>
                  <Box flex="1" minW={0}>
                    <Text fontSize="12.5px" color={c.text} noOfLines={1}>{q.question_text}</Text>
                    <Text fontSize="11px" color={c.textMuted}>{questionLabel(q)} · {q.total_mark} {q.total_mark === 1 ? "mark" : "marks"}</Text>
                  </Box>
                  {i > 0 && (
                    <IconAction label="Move up" icon={HiOutlineArrowUp}
                      onClick={() => setPicked((prev) => { const n = [...prev]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })} />
                  )}
                  <IconAction label="Take out" icon={HiOutlineXMark} onClick={() => toggle(q)} />
                </Flex>
              ))}
            </Flex>
          </Box>

          <Box>
            <Eyebrow mb="8px">How should it land?</Eyebrow>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap="8px">
              {MODES.map((m) => {
                const on = mode === m.id;
                return (
                  <Flex
                    key={m.id}
                    as="button"
                    type="button"
                    onClick={() => setMode(m.id)}
                    aria-pressed={on}
                    direction="column"
                    align="flex-start"
                    gap="6px"
                    p="12px"
                    textAlign="left"
                    borderRadius="14px"
                    bg={on ? c.surface : c.glassEdge}
                    border="1px solid"
                    borderColor={on ? c.solidBg : c.borderSoft}
                    boxShadow={on ? `0 0 0 1px ${c.solidBg}` : "none"}
                    _hover={on ? {} : { bg: c.surface }}
                  >
                    <Flex boxSize="28px" borderRadius="9px" align="center" justify="center" bg={on ? c.solidBg : c.surfaceSubtle} color={on ? c.solidText : c.textSub}>
                      <Icon as={m.icon} boxSize="15px" />
                    </Flex>
                    <Text fontSize="13px" fontWeight="600" color={c.text}>{m.title}</Text>
                    <Text fontSize="11.5px" color={c.textSub} lineHeight="1.4">{m.line}</Text>
                  </Flex>
                );
              })}
            </Box>
            {tooMany && (
              <Text fontSize="12px" color={c.accentSoftText} mt="8px">
                An escape room has up to {modeInfo.max} locks — take out {picked.length - modeInfo.max}.
              </Text>
            )}
          </Box>

          {mode !== "BOARD" && (
            <Flex align="center" justify="space-between" p="10px 12px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
              <Box>
                <Text fontSize="13px" fontWeight="500" color={c.text}>Timer</Text>
                <Text fontSize="11px" color={c.textMuted}>
                  {mode === "QUIZ_RACE" ? "Per question; answers close at zero" : "Shown to everyone as a guide"}
                </Text>
              </Box>
              <Flex gap="2px" p="3px" borderRadius="200px" bg={c.surfaceSubtle}>
                {TIMERS.map((t) => (
                  <Box
                    key={t.label}
                    as="button"
                    type="button"
                    onClick={() => setTimer(t.value)}
                    px="11px"
                    py="5px"
                    borderRadius="200px"
                    fontSize="12px"
                    fontWeight={timer === t.value ? "600" : "400"}
                    whiteSpace="nowrap"
                    bg={timer === t.value ? c.solidBg : "transparent"}
                    color={timer === t.value ? c.solidText : c.textSub}
                  >
                    {t.label}
                  </Box>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        <Box px="16px" pb="16px" pt="6px">
          {error && <Text fontSize="12px" color={c.accentSoftText} mb="8px" role="alert">{error}</Text>}
          <Flex gap="8px">
            <Button {...quietButton} h="42px" borderRadius="12px" fontSize="13px" fontWeight="500"
              leftIcon={<Icon as={HiOutlineArrowLeft} boxSize="14px" />} onClick={() => setStep("pick")}>
              Questions
            </Button>
            <Button
              {...blueButton}
              flex="1"
              h="42px"
              borderRadius="12px"
              fontSize="14px"
              fontWeight="600"
              isLoading={busy}
              isDisabled={!picked.length || tooMany}
              onClick={() =>
                mode === "BOARD"
                  ? onDropOnBoard(picked)
                  : onLaunch({ kind: mode, question_guids: picked.map((q) => q.guid), timer_seconds: timer })
              }
            >
              {mode === "BOARD" ? "Drop on the board" : "Launch to the class"}
            </Button>
          </Flex>
          <Text fontSize="11px" color={c.textMuted} textAlign="center" mt="8px">
            {mode === "BOARD" ? "They land where you are looking." : "Students see it straight away, on their own screens."}
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" minH={0} flex="1 1 auto">
      {onBack && (
        <Flex align="center" justify="space-between" mx="16px" mb="10px" p="8px 10px" borderRadius="12px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
          <Text fontSize="12px" color={c.textSub}>
            {replacing ? "Launching this ends the one running now." : "Your last activity's results stay in Activities."}
          </Text>
          <Button size="xs" variant="ghost" fontSize="12px" color={c.text} onClick={onBack} flexShrink={0}>
            Back to it
          </Button>
        </Flex>
      )}
      <Flex direction="column" gap="10px" px="16px" pb="10px">
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none" h="38px">
            <Icon as={HiOutlineMagnifyingGlass} boxSize="15px" color={c.textMuted} />
          </InputLeftElement>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={bank?.subject_name ? `Search ${bank.subject_name} questions` : "Search the question bank"}
            h="38px"
            borderRadius="12px"
            bg={c.surface}
            borderColor={c.borderSoft}
            fontSize="13px"
            color={c.text}
            _placeholder={{ color: c.textMuted }}
            _focusVisible={{ borderColor: c.solidBg, boxShadow: "none" }}
          />
        </InputGroup>
        {bank?.topics?.length > 0 && (
          <Flex gap="6px" overflowX="auto" pb="2px" data-own-scroll sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {[{ guid: null, name: "All topics" }, ...bank.topics].map((t) => {
              const on = topic === t.guid;
              return (
                <Box
                  key={t.guid || "all"}
                  as="button"
                  type="button"
                  onClick={() => setTopic(t.guid)}
                  flexShrink={0}
                  px="12px"
                  h="30px"
                  borderRadius="200px"
                  fontSize="12px"
                  fontWeight={on ? "600" : "400"}
                  bg={on ? c.solidBg : c.surface}
                  color={on ? c.solidText : c.text}
                  border="1px solid"
                  borderColor={on ? c.solidBg : c.borderSoft}
                >
                  {t.name}
                </Box>
              );
            })}
          </Flex>
        )}
      </Flex>

      <Flex direction="column" gap="8px" px="16px" pb="10px" flex="1 1 auto" minH={0} overflowY="auto" data-own-scroll>
        {loading && !bank && (
          <Flex align="center" justify="center" gap="8px" py="30px" color={c.textSub}>
            <Spinner size="sm" />
            <Text fontSize="13px">Opening the question bank…</Text>
          </Flex>
        )}
        {bankError && <Text fontSize="12.5px" color={c.accentSoftText}>{bankError}</Text>}
        {bank && bank.questions.length === 0 && (
          <Text fontSize="13px" color={c.textSub} py="20px" textAlign="center">
            {bank.subject_name ? "Nothing matches. Try other words or another topic." : "This class has no subject yet, so there is no bank to pull from."}
          </Text>
        )}
        {bank?.questions.map((q) => (
          <BankCard key={q.guid} q={q} isPicked={pickedIds.has(q.guid)} onToggle={() => toggle(q)} />
        ))}
      </Flex>

      {/* What is picked, and on to how it lands. */}
      <Flex align="center" gap="10px" mx="12px" mb="12px" p="8px 8px 8px 14px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
        <Box flex="1" minW={0}>
          <Text fontSize="13px" fontWeight="600" color={c.text}>
            {picked.length ? `${picked.length} ${picked.length === 1 ? "question" : "questions"}` : "Pick questions"}
          </Text>
          <Text fontSize="11px" color={c.textMuted}>
            {picked.length ? `${marks} ${marks === 1 ? "mark" : "marks"}` : "Tap a question to add it"}
          </Text>
        </Box>
        <Button {...inkButton} h="36px" px="18px" borderRadius="11px" fontSize="13px" fontWeight="500"
          isDisabled={!picked.length} onClick={() => setStep("launch")}>
          Next
        </Button>
      </Flex>
    </Flex>
  );
};

const IconAction = ({ label, icon, onClick }) => (
  <Flex
    as="button"
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    boxSize="26px"
    borderRadius="full"
    align="center"
    justify="center"
    color={c.textMuted}
    flexShrink={0}
    _hover={{ bg: c.hover, color: c.text }}
  >
    <Icon as={icon} boxSize="14px" />
  </Flex>
);

export default ActivityStudio;
